"use client";

import { useState } from "react";
import { ArrowLeftRight } from "lucide-react";
import { CHAINS, FIAT, STABLECOINS, fmtAmount } from "@/lib/format";
import { CurrencyBadge, CurrencySelect } from "@/components/currency";
import SourceSelect from "@/components/source-select";
import type { PayoutSource } from "./payout-form";
import type { PayoutInitialValues } from "./payout-form";

/**
 * XE-style entry point: how much, in what currency, from where, to whom
 * (currency only) — before any of the recipient/purpose/compliance detail.
 * Bidirectional: the amount can be specified on either side ("you send" or
 * "recipient gets"); whichever one the user typed into most recently is the
 * locked side, matching Conduit's order API (one amount + a lockSide enum,
 * not two independently-computed numbers — there's no rate-preview endpoint
 * to keep both fields live-synced for cross-currency amounts).
 */
export default function PayoutStart({
  sources,
  initialSourceValue,
  onContinue,
}: {
  sources: PayoutSource[];
  /** Pre-selects a funding source — deep-linked from the Account page's "Withdraw" buttons. */
  initialSourceValue?: string;
  onContinue: (values: PayoutInitialValues) => void;
}) {
  const [sourceValue, setSourceValue] = useState(
    initialSourceValue && sources.some((s) => s.value === initialSourceValue)
      ? initialSourceValue
      : (sources[0]?.value ?? ""),
  );
  const [kind, setKind] = useState<"fiat" | "crypto">("fiat");
  const [code, setCode] = useState("USD");
  const [chain, setChain] = useState("base");
  const [amount, setAmount] = useState("");
  const [lockSide, setLockSide] = useState<"source" | "destination">("destination");
  // Unfunded payouts: only cross-currency sends can wait for funds — they go
  // through /orders (autoExecute), while same-asset payouts hard-require the
  // balance up front (POST /payouts has no autoExecute field at all).
  const [autoExecute, setAutoExecute] = useState(false);

  const source = sources.find((s) => s.value === sourceValue);
  const crossCurrency = !!source?.code && source.code !== code;
  const codes = kind === "crypto" ? STABLECOINS : FIAT;
  // "Wait for funds" is only offered when the source genuinely can't cover
  // the amount — funded sends just settle instantly. The amount may be typed
  // on either side, but every pair here is USD↔stablecoin at ~1:1, so the
  // comparison holds for both lock sides.
  const insufficient = !!source && !!amount && source.available !== undefined && Number(amount) > source.available;

  // Same currency: both fields always mirror the one number (trivially 1:1).
  // Cross-currency: only the locked side shows the typed number; the other
  // is left blank with an "auto" placeholder rather than a stale/guessed value.
  const sendValue = lockSide === "source" ? amount : crossCurrency ? "" : amount;
  const recipientValue = lockSide === "destination" ? amount : crossCurrency ? "" : amount;

  function setSendAmount(v: string) {
    setLockSide("source");
    setAmount(v);
  }
  function setRecipientAmount(v: string) {
    setLockSide("destination");
    setAmount(v);
  }

  function swap() {
    // Best-effort swap: find a source already holding the destination currency.
    const match = sources.find((s) => s.code === code);
    if (match && source) {
      setSourceValue(match.value);
      setCode(source.code!);
      setKind((STABLECOINS as readonly string[]).includes(source.code!) ? "crypto" : "fiat");
      setLockSide((s) => (s === "source" ? "destination" : "source"));
    }
  }

  function toggleKind(k: "fiat" | "crypto") {
    setKind(k);
    setCode(k === "crypto" ? "USDC" : "USD");
  }

  const activeCode = lockSide === "source" ? (source?.code ?? code) : code;

  return (
    <div className="card rise h-full p-8">
      <div className="card-cream p-6">
        <div className="eyebrow mb-2" style={{ color: "var(--c-ink-soft)" }}>
          you send
        </div>
        {sources.length === 0 ? (
          <p className="text-[13px] font-light" style={{ color: "var(--c-ink-soft)" }}>
            No funding sources yet — provision a virtual account or wallet on the Account page first.
          </p>
        ) : (
          <>
            <SourceSelect items={sources} value={sourceValue} onChange={setSourceValue} />
            <input
              className="field mt-3 text-[28px]"
              style={{ fontWeight: 400 }}
              inputMode="decimal"
              placeholder={crossCurrency && lockSide !== "source" ? "auto" : "0.00"}
              value={sendValue}
              onChange={(e) => setSendAmount(e.target.value)}
            />
          </>
        )}
      </div>

      <div className="my-3 flex justify-center">
        <button
          type="button"
          title="Swap"
          className="flex h-10 w-10 items-center justify-center rounded-full transition-transform duration-150 hover:rotate-180"
          style={{ background: "var(--c-green)" }}
          onClick={swap}
        >
          <ArrowLeftRight size={16} />
        </button>
      </div>

      <div className="card-cream p-6">
        <div className="mb-2 flex items-center justify-between">
          <div className="eyebrow" style={{ color: "var(--c-ink-soft)" }}>
            recipient gets
          </div>
          <div className="flex gap-1">
            {(["fiat", "crypto"] as const).map((k) => (
              <button
                key={k}
                type="button"
                className="pill"
                style={
                  kind === k
                    ? { background: "var(--c-charcoal)", color: "var(--c-white)" }
                    : { boxShadow: "inset 0 0 0 1px var(--c-hairline)", color: "var(--c-ink-soft)" }
                }
                onClick={() => toggleKind(k)}
              >
                {k}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input
            className="field text-[28px]"
            style={{ fontWeight: 400 }}
            inputMode="decimal"
            placeholder={crossCurrency && lockSide !== "destination" ? "auto" : "0.00"}
            value={recipientValue}
            onChange={(e) => setRecipientAmount(e.target.value)}
          />
          <div className="w-[168px] shrink-0">
            <CurrencySelect codes={codes} value={code} onChange={setCode} />
          </div>
        </div>
        {kind === "crypto" && (
          <select className="field mt-3" value={chain} onChange={(e) => setChain(e.target.value)}>
            {CHAINS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="mt-4 flex items-center gap-2 text-[13px] font-light" style={{ color: "var(--c-ink-soft)" }}>
        {source?.code && (
          <>
            <CurrencyBadge code={source.code} size={16} />
            <span>
              {source.code}
              {crossCurrency
                ? lockSide === "source"
                  ? ` → ${code}: recipient's exact amount confirmed on the next step.`
                  : ` → ${code}: your exact debit amount confirmed on the next step.`
                : " · sent as-is, no conversion needed."}
            </span>
          </>
        )}
      </div>

      {insufficient && crossCurrency && (
        <label className="mt-3 flex cursor-pointer items-start gap-2 text-[12px] font-light">
          <input
            type="checkbox"
            className="mt-[2px]"
            checked={autoExecute}
            onChange={(e) => setAutoExecute(e.target.checked)}
          />
          <span>
            {source?.code} balance is short ({fmtAmount(source?.available, source?.code)} available) — execute
            automatically when the funds arrive.
            <span style={{ color: "var(--c-ink-soft)" }}>
              {" "}
              The rate still locks at approval; Conduit runs the conversion the moment the balance covers it.
            </span>
          </span>
        </label>
      )}
      {insufficient && !crossCurrency && (
        <p className="mt-3 text-[12px] font-light" style={{ color: "var(--c-danger)" }}>
          Not enough {source?.code} in this source ({fmtAmount(source?.available, source?.code)} available) — deposit
          first. Same-currency payouts can&apos;t wait for funds; Conduit rejects them with insufficient funds.
        </p>
      )}

      <button
        className="btn btn-primary mt-6 w-full"
        disabled={!amount || Number(amount) <= 0 || sources.length === 0}
        onClick={() =>
          onContinue({ kind, code, amount, sourceValue, chain, lockSide, autoExecute: crossCurrency && insufficient && autoExecute })
        }
      >
        Send {amount ? `${fmtAmount(amount)} ${activeCode}` : ""}
      </button>
    </div>
  );
}
